import { createAction, lt, showErrorAlert, showInfoAlert } from '@mews/framework';
import { CommanderThunkAction } from '@mews-commander/types';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { getBillOwnerId, isBillVirtual } from '@mews-commander/legacy-entities/src/ducks/bills';
import { getPreauthorization } from '@mews-commander/legacy-entities/src/ducks/preauthorizations';
import { FULL_BILL_EXTENT } from '@mews-commander/legacy-entities/src/actions/bills';
import { addCreditCardPayment } from '../../payment/actions/api';
import { refetchBill } from '../ducks/bills';
import { openAccountBillingModal, openCloseBill } from '../ducks/navigation';
import { PAYMENT_FETCH_FAILED, PAYMENT_FETCH_STARTED, PAYMENT_FETCH_SUCCEEDED } from '../ducks/payments';
import { getAmountDue } from '../selectors/balances';
import { isPaymentVerifyingState } from '../utils';
import { logBillingAction } from '../utils/logBillingAction';
import { getRealBillId } from './utils';

const showPaymentVerifyingAlert = (payment: $TSFixMe) => {
    if (isPaymentVerifyingState(payment)) {
        showInfoAlert({ description: lt('VerifyingPaymentResultMessage') });
    }
};

const chargeGatewayCreditCard =
    ({ billId, scope, ...other }: $TSFixMe) =>
    (dispatch: $TSFixMe) => {
        dispatch(createAction(PAYMENT_FETCH_STARTED, undefined, { scope }));

        return dispatch(addCreditCardPayment({ ...other, billId, isGateway: true }))
            .then(({ value }: $TSFixMe) => {
                dispatch(createAction(PAYMENT_FETCH_SUCCEEDED, undefined, { scope }));
                showPaymentVerifyingAlert(value);
                dispatch(openCloseBill({ billId, scope }));
            })
            .catch((error: $TSFixMe) => {
                dispatch(createAction(PAYMENT_FETCH_FAILED, undefined, { scope }));
                throw error;
            });
    };

const chargePreauthorization =
    ({ preauthorizationAmount, scope, ...data }: $TSFixMe): CommanderThunkAction =>
    (dispatch: $TSFixMe, _) => {
        const { billId, ...other } = data;
        dispatch(createAction(PAYMENT_FETCH_STARTED, undefined, { scope }));

        return dispatch(
            addCreditCardPayment({
                ...other,
                billId,
                amount: data.amount,
                isGateway: true,
            })
        )
            .then(({ value }: $TSFixMe) => {
                const shouldRedirectToCloseBillPage = value.amount === data.amount;

                dispatch(createAction(PAYMENT_FETCH_SUCCEEDED, undefined, { scope }));
                showPaymentVerifyingAlert(value);

                if (shouldRedirectToCloseBillPage) {
                    dispatch(openCloseBill({ billId, scope }));
                } else {
                    dispatch(openAccountBillingModal({ accountId: data.customerId }));
                }
            })
            .catch((error: $TSFixMe) => {
                dispatch(createAction(PAYMENT_FETCH_FAILED, undefined, { scope }));
                throw error;
            });
    };

const validateBillAmountDue =
    ({ billId, scope }: $TSFixMe) =>
    async (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const state = getState();
        const accountId = getBillOwnerId(state, { id: billId });
        const enterpriseId = getCurrentEnterpriseId();
        const { grossValue: originalGrossValue, currency: originCurrency } = getAmountDue(state, {
            billIds: [billId],
            enterpriseId,
        });

        if (isBillVirtual(state, { id: billId })) {
            await dispatch(refetchBill({ accountId, scope, extent: FULL_BILL_EXTENT }));
        } else {
            await dispatch(refetchBill({ accountId, billId, scope, extent: FULL_BILL_EXTENT }));
        }

        const { grossValue, currency } = getAmountDue(getState(), { billIds: [billId], enterpriseId });

        if (originalGrossValue !== grossValue || originCurrency !== currency) {
            throw Error('Bill amount due does not match');
        }

        const finalBillId = await dispatch(getRealBillId({ billId, scope }));
        return { grossValue, currency, finalBillId, accountId };
    };

export const handlePaymentCardClick =
    ({ creditCardId, billId, scope }: $TSFixMe) =>
    async (dispatch: $TSFixMe) => {
        try {
            const { grossValue, currency, finalBillId, accountId } = await dispatch(
                validateBillAmountDue({ billId, scope })
            );
            dispatch(logBillingAction('Payment card charged'));

            return dispatch(
                chargeGatewayCreditCard({
                    customerId: accountId,
                    creditCardId,
                    billId: finalBillId,
                    amount: grossValue,
                    currency,
                    scope,
                })
            );
        } catch (err) {
            showErrorAlert({ description: lt('BillCloseTimeErrorMessage') });
        }
    };

export const handlePreauthorizationClick =
    ({ preauthorizationId, billId, scope }: $TSFixMe) =>
    async (dispatch: $TSFixMe, getState: $TSFixMe) => {
        try {
            const { grossValue, currency, finalBillId, accountId } = await dispatch(
                validateBillAmountDue({ billId, scope })
            );
            dispatch(logBillingAction('Preauhtorization charged'));

            const { amount: preauthorizationAmount } =
                getPreauthorization(getState(), {
                    ownerId: accountId,
                    id: preauthorizationId,
                }) || {};
            return dispatch(
                chargePreauthorization({
                    customerId: accountId,
                    preauthorizationAmount,
                    preauthorizationId,
                    billId: finalBillId,
                    amount: grossValue,
                    currency,
                    scope,
                })
            );
        } catch (err) {
            showErrorAlert({ description: lt('BillCloseTimeErrorMessage') });
        }
    };
